import { FC, useMemo, useRef, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useQualificationWaves } from '@/hooks/queries/use-qualification-waves';
import { useUpdateWave } from '@/hooks/mutations/use-wave-admin';
import { BoardRow, QualificationWave } from '@/services/workflow/qualification';
import { WaveEditorModal } from './wave-editor-modal';
import { ApproversPanel } from './approvers-panel';
import { cn } from '@/lib/utils';

const STATUS_TONES: Record<QualificationWave['status'], string> = {
  planned: 'bg-muted text-foreground',
  open: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  closed: 'bg-muted text-muted-foreground',
};

const formatWindow = (from: string, to: string) => {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    });
  return `${fmt(from)} to ${fmt(to)}`;
};

interface WavesPanelProps {
  client: string;
  region: string;
  /** Board rows, so each wave can show how much is actually in it. */
  rows: BoardRow[];
}

export const WavesPanel: FC<WavesPanelProps> = ({ client, region, rows }) => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState<QualificationWave | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [approversFor, setApproversFor] = useState<QualificationWave | null>(
    null,
  );

  const { data: waves, isLoading } = useQualificationWaves(client, region);
  const updateWave = useUpdateWave();

  const counts = useMemo(() => {
    const byWave = new Map<string, number>();
    rows.forEach((row) => {
      if (!row.wave_id) return;
      byWave.set(row.wave_id, (byWave.get(row.wave_id) ?? 0) + 1);
    });
    return byWave;
  }, [rows]);

  const open = (wave: QualificationWave | null) => {
    setEditing(wave);
    setIsEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading waves...
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='space-y-3'>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <h2 className='text-base font-semibold'>Waves</h2>
            <p className='text-sm text-muted-foreground'>
              A wave is the window a workflow can enter, and the conditions it
              must pass. Both are data: changing them needs no deploy.
            </p>
          </div>
          <Button onClick={() => open(null)} className='gap-2'>
            <Plus className='size-4' />
            New wave
          </Button>
        </div>

        {!waves?.length ? (
          <p className='text-sm text-muted-foreground'>
            No waves for {client} in {region} yet. Nothing can be applied until
            one is open.
          </p>
        ) : (
          <div className='overflow-x-auto'>
            <Table tableRef={tableRef}>
              <TableHeader>
                <TableRow>
                  <TableHead>Wave</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Pre-UAT</TableHead>
                  <TableHead>UAT</TableHead>
                  <TableHead>Conditions</TableHead>
                  <TableHead>Sign-off</TableHead>
                  <TableHead>In it</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {waves.map((wave) => (
                  <TableRow key={wave.id}>
                    <TableCell className='font-medium'>{wave.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant='secondary'
                        className={cn(STATUS_TONES[wave.status])}
                      >
                        {wave.status}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatWindow(
                        wave.pre_uat_opens_at,
                        wave.pre_uat_closes_at,
                      )}
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatWindow(wave.uat_opens_at, wave.uat_closes_at)}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {wave.required_checks.length}
                      {wave.advisory_checks.length
                        ? ` + ${wave.advisory_checks.length} advisory`
                        : ''}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {wave.requires_client_sign_off
                        ? 'Capitol and client'
                        : 'Capitol only'}
                    </TableCell>
                    <TableCell
                      className='text-sm tabular-nums'
                      data-testid='wave-in-it'
                    >
                      {counts.get(wave.id) ?? 0}
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex justify-end gap-2'>
                        {wave.status !== 'open' && (
                          <Button
                            variant='outline'
                            size='sm'
                            disabled={updateWave.isPending}
                            onClick={() =>
                              updateWave.mutate({
                                waveId: wave.id,
                                patch: { status: 'open' },
                              })
                            }
                          >
                            Open
                          </Button>
                        )}
                        {wave.status === 'open' && (
                          <Button
                            variant='outline'
                            size='sm'
                            disabled={updateWave.isPending}
                            onClick={() =>
                              updateWave.mutate({
                                waveId: wave.id,
                                patch: { status: 'closed' },
                              })
                            }
                          >
                            Close
                          </Button>
                        )}
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() =>
                            setApproversFor(
                              approversFor?.id === wave.id ? null : wave,
                            )
                          }
                        >
                          {approversFor?.id === wave.id
                            ? 'Hide approvers'
                            : 'Approvers'}
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => open(wave)}
                        >
                          Amend
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className='text-xs text-muted-foreground'>
          Closing a wave stops new entries. It does not stop sign-off on what is
          already in pre-UAT, because reviews take days and windows are days
          long.
        </p>
      </div>

      {approversFor && (
        <ApproversPanel
          client={client}
          region={region}
          waveId={approversFor.id}
          waveName={approversFor.name}
        />
      )}

      <ApproversPanel client={client} region={region} />

      <WaveEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        client={client}
        region={region}
        wave={editing}
      />
    </div>
  );
};
